import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HeartIcon, StudentIcon } from "../lib/icons";
import { LineReveal, FadeUp } from "./Reveal";
import { EmSwash } from "./EmSwash";
import { HERO_SLIDES, IMAGES } from "../data/content";
import { PaperLight } from "./PaperLight";

const ROTATE_MS = 6000;

/** Placeholder tile for a slide that doesn't have a photo yet (e.g. graduation).
 *  Warm beige with the graduation-cap glyph and an italic serif "coming soon". */
const GraduationPlaceholder = () => (
  <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-beige px-8 text-center">
    <div
      className="absolute inset-0 opacity-40"
      style={{
        backgroundImage:
          "repeating-linear-gradient(45deg, transparent, transparent 14px, rgba(253,208,6,0.35) 14px, rgba(253,208,6,0.35) 15px)",
      }}
    />
    <StudentIcon className="relative h-24 w-24 text-tangerine" />
    <p className="relative mt-6 font-serif text-3xl italic leading-snug text-ink">
      Graduation day
    </p>
    <span className="relative mt-3 inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-bone px-3 py-1 text-[13px] font-medium uppercase tracking-[0.2em] text-olive">
      Photo coming soon
    </span>
  </div>
);

export const Hero = ({ onStart, onDonate, primaryLabel = "Preview the platform" }) => {
  const ref = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slide = HERO_SLIDES[index];

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  // Auto-rotate every ROTATE_MS unless paused (hover)
  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [paused]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative w-full overflow-hidden bg-sand pt-28 pb-20 lg:pt-40 lg:pb-24"
      data-testid="hero-section"
    >
      {/* "Paper & Light" WebGL ground — grain + slow daylight sweep. Flag: PAPER_LIGHT_ENABLED in PaperLight.jsx */}
      <PaperLight />

      {/* Subtle brand-mark pattern behind the copy column (desktop only) */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 hidden w-[48%] opacity-[0.035] mix-blend-multiply lg:block"
        style={{
          backgroundImage: `url(${IMAGES.pattern})`,
          backgroundSize: "460px",
          backgroundRepeat: "repeat",
          WebkitMaskImage:
            "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 88%, rgba(0,0,0,0) 100%)",
          WebkitMaskComposite: "source-in",
          maskImage:
            "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 88%, rgba(0,0,0,0) 100%)",
          maskComposite: "intersect",
        }}
        aria-hidden="true"
        data-testid="hero-pattern-watermark"
      />

      {/* Brand entrance — moved BELOW the hero grid. See end of section for placement. */}

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-12 lg:gap-x-12 lg:px-8">
        {/* Copy */}
        <div className="lg:col-span-5">
          <h1 className="font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.94] tracking-tight text-ink">
            <LineReveal
              lines={["Think back;", <EmSwash key="i">Pay it forward.</EmSwash>]}
              delay={0.15}
              stagger={0.22}
            />
          </h1>

          {/* Editorial deck — the retired hero-marquee's best phrase, given a
              still home directly under the H1. Serif italic, ink, one line. */}
          <FadeUp delay={0.75} tier="lg" className="mt-6 max-w-xl">
            <p
              data-testid="hero-deck"
              className="font-serif text-3xl italic leading-tight text-ink"
            >
              For the costs financial aid misses.
            </p>
          </FadeUp>

          <FadeUp delay={1.15} tier="lg" className="mt-6 max-w-xl">
            <p className="text-base leading-relaxed text-olive">
              My Rich Aunt connects everyday donors with verified college and graduate students. Every gift is tax-deductible.
            </p>
          </FadeUp>

          <FadeUp delay={1.55} tier="lg">
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {onDonate && (
                <button
                  data-testid="hero-give-button"
                  onClick={onDonate}
                  className="group inline-flex items-center gap-2 rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest"
                >
                  <HeartIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
                  Give to the General Fund
                </button>
              )}
              <button
                data-testid="hero-preview-button"
                onClick={onStart}
                className={`group inline-flex items-center gap-2 rounded-full px-6 py-4 text-base font-semibold transition-colors ${onDonate ? "border-2 border-ink bg-transparent text-ink hover:bg-ink hover:text-sand" : "bg-ink text-sand hover:bg-forest"}`}
              >
                {primaryLabel}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
              </button>
            </div>
          </FadeUp>
        </div>

        {/* Photo — layered stacked frames (yellow + cobalt) offset in opposite directions
            so the colored peeks appear on all four edges of the photo.
            Reference: Aperture / Kinfolk gallery-print treatment.
            Shifted to col-start-7 (with col-span-6) so a full empty grid
            column sits between the copy and the photo — an editorial gutter
            that lets both columns breathe. */}
        <div className="relative lg:col-span-6 lg:col-start-7">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 hidden translate-x-4 -translate-y-4 rounded-md bg-yellow lg:block"
            data-testid="hero-photo-frame-accent-primary"
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 hidden -translate-x-5 translate-y-5 rounded-md bg-cobalt/85 lg:block"
            data-testid="hero-photo-frame-accent-secondary"
          />

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
            className="relative z-10 overflow-hidden rounded-md border border-border shadow-[0_30px_60px_-30px_rgba(26,23,20,0.4)]"
            data-testid="hero-photo-frame"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {/* Aspect-ratio spacer — keeps frame stable while photos cross-fade */}
            <div className="relative aspect-[4/5] w-full lg:aspect-[5/6]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1 }}
                  transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute inset-0"
                  data-testid={`hero-slide-${index}`}
                >
                  {slide.placeholder ? (
                    <div className="relative h-full w-full">
                      <GraduationPlaceholder />
                    </div>
                  ) : (
                    <motion.img
                      style={{ y }}
                      src={slide.src}
                      alt={slide.alt}
                      className="h-full w-full scale-105 object-cover object-center"
                      data-testid="hero-photo"
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Scrim + caption (skip scrim on placeholder since bg is beige not photo) */}
            {!slide.placeholder && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/60 via-ink/20 to-transparent" />
            )}
            <AnimatePresence mode="wait">
              <motion.figcaption
                key={slide.caption}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                className={`absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 font-serif text-3xl italic leading-snug ${ slide.placeholder ? "text-ink/75" : "text-sand" }`}
                data-testid="hero-caption"
              >
                <span>{slide.caption}</span>
              </motion.figcaption>
            </AnimatePresence>

            {/* Slide progress dots — subtle, top-right */}
            <div
              className="absolute right-4 top-4 z-20 flex items-center gap-1.5"
              role="tablist"
              aria-label="Hero photo rotation"
            >
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Show hero slide ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-6 bg-sand" : "w-1.5 bg-sand/50 hover:bg-sand/80"
                  }`}
                  data-testid={`hero-slide-dot-${i}`}
                />
              ))}
            </div>
          </motion.figure>
        </div>
      </div>

      {/* Signature-reveal band — plays as the visitor scrolls, "ta-dah" moment.
          Full-width, marquee-scale wordmark + all 5 palette jewels. Replaces
          the retired Caveat "Introducing, My Rich Aunt" scroll cue. */}
    </section>
  );
};
